'use client';

import { useCallback } from 'react';
import { useLocalStorage } from './use-local-storage';
import { useUser } from '@/features/auth/model/auth.selectors';
import { ANONYMOUS_SCOPE } from '@/shared/lib/user-scoped-storage';

interface LastProjectData {
  projectId: string;
  timestamp: number;
}

/**
 * Hook to persist and retrieve the last opened project.
 * Used to show project in sidebar when on Dashboard.
 * Key is scoped per user — same `${userId}:` prefix as the rest of the
 * user-scoped localStorage entries, so logout can wipe everything in one
 * pass (see `wipeUserScopedKeys` in `user-scoped-storage.ts`).
 */
export function useLastProject() {
  const userId = useUser()?.id;
  const storageKey = `${userId ?? ANONYMOUS_SCOPE}:facade:lastOpenedProjectId`;
  const [lastProject, setLastProject, removeLastProject] =
    useLocalStorage<LastProjectData | null>(storageKey, null);

  const updateLastProject = useCallback(
    (projectId: string) => {
      setLastProject({ projectId, timestamp: Date.now() });
    },
    [setLastProject]
  );

  const clearLastProject = useCallback(() => {
    removeLastProject();
  }, [removeLastProject]);

  return {
    lastProjectId: lastProject?.projectId ?? null,
    updateLastProject,
    clearLastProject,
  };
}
